"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.projectSnapshot = exports.CRUD_PROJECTION_FORCED_KEYS = void 0;
const projection_zod_1 = require("./projection-zod");
/**
 * Применение проекции к УЖЕ построенному snapshot-объекту (data-уровень,
 * browser-safe). Используется: get-handler (O11 — prune после rowToSnapshot,
 * работает поверх любых findById-override'ов) и useLiveCollection
 * (client-side проекция live-событий → коллекция гомогенна с bootstrap'ом).
 *
 * Инварианты паритета с SQL-сборкой list (см. projection-sql.ts):
 * - id/tenantId присутствуют всегда (форс-инклюд, O5);
 * - include-режим: поле, запрошенное путями, всегда присутствует контейнером
 *   (пустым `{}`, если значений нет);
 * - JSON null по include-пути неотличим от отсутствия → ключ опускается (O7).
 */
exports.CRUD_PROJECTION_FORCED_KEYS = ['id', 'tenantId'];
const isPlainObject = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
const getDeep = (root, segments) => {
    let current = root;
    for (const seg of segments) {
        if (!isPlainObject(current))
            return undefined;
        current = current[seg];
    }
    return current;
};
const setDeep = (target, segments, value) => {
    // Guard: reserved-ключ на любом сегменте → не пишем (proto-pollution).
    // Грамматика/SQL уже отбраковывают такие пути; это последний эшелон на случай
    // прямого вызова projectSnapshot (browser-bus) с непровалидированным путём.
    if (segments.some((seg) => projection_zod_1.DANGEROUS_PROJECTION_SEGMENTS.has(seg)))
        return;
    let current = target;
    for (const seg of segments.slice(0, -1)) {
        const next = current[seg];
        if (isPlainObject(next)) {
            current = next;
        }
        else {
            const created = {};
            current[seg] = created;
            current = created;
        }
    }
    current[segments[segments.length - 1]] = value;
};
/** Иммутабельное удаление пути: клонирует только затронутую ветку. */
const removeDeep = (root, segments) => {
    const [head, ...rest] = segments;
    if (head === undefined || !(head in root))
        return root;
    const clone = { ...root };
    if (rest.length === 0) {
        delete clone[head];
        return clone;
    }
    const child = clone[head];
    if (!isPlainObject(child))
        return root;
    clone[head] = removeDeep(child, rest);
    return clone;
};
const projectSnapshot = (item, proj) => {
    // Пустая проекция (null / [] / undefined) = «маски нет» — паритет с
    // нормализацией в контракте (crudProjectionField) и applyProjection.
    const fields = (0, projection_zod_1.isEmptyProjection)(proj.fields) ? undefined : proj.fields;
    const fieldsOmit = (0, projection_zod_1.isEmptyProjection)(proj.fieldsOmit) ? undefined : proj.fieldsOmit;
    if (fields !== undefined) {
        const out = {};
        const wholeHeads = new Set(exports.CRUD_PROJECTION_FORCED_KEYS); // O5: форс-инклюд
        const pathsByHead = new Map();
        for (const raw of fields) {
            const { head, segments } = (0, projection_zod_1.splitProjectionPath)(raw);
            if (segments.length === 0) {
                wholeHeads.add(head);
            }
            else {
                const list = pathsByHead.get(head) ?? [];
                list.push(segments);
                pathsByHead.set(head, list);
            }
        }
        for (const head of wholeHeads) {
            if (head in item)
                out[head] = item[head];
        }
        for (const [head, paths] of pathsByHead) {
            if (wholeHeads.has(head))
                continue; // целое поле побеждает пути
            const container = {};
            for (const segments of (0, projection_zod_1.pruneSubsumedPaths)(paths)) {
                const value = getDeep(item[head], segments);
                if (value === undefined || value === null)
                    continue; // O7
                setDeep(container, segments, value);
            }
            out[head] = container; // контейнер присутствует всегда (паритет с SQL)
        }
        return out;
    }
    if (fieldsOmit !== undefined) {
        let out = { ...item };
        for (const raw of fieldsOmit) {
            const { head, segments } = (0, projection_zod_1.splitProjectionPath)(raw);
            if (exports.CRUD_PROJECTION_FORCED_KEYS.includes(head) && segments.length === 0) {
                continue; // O5: id/tenantId не вычёркиваются
            }
            if (segments.length === 0) {
                delete out[head];
            }
            else if (isPlainObject(out[head])) {
                out = { ...out, [head]: removeDeep(out[head], segments) };
            }
        }
        return out;
    }
    return item;
};
exports.projectSnapshot = projectSnapshot;
//# sourceMappingURL=projection-apply.js.map